import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, BookOpen, Layers, Clock, Star, ChevronRight,
  Zap, Trophy, Trash2, ArrowLeft, Share2, Gem, X, Loader2, Copy, CheckCircle2,
} from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPublish, setShowPublish] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}`).then(r => {
      setCourse(r.data);
      setLoading(false);
    }).catch(() => { toast.error("Course not found"); nav("/app/courses"); });
  }, [id, nav]);

  const startExam = async () => {
    try {
      const { data } = await api.post(`/courses/${id}/exam`, { duration_minutes: 90, mode: "typed" });
      nav(`/app/exam/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start exam");
    }
  };

  const deleteCourse = async () => {
    if (!window.confirm("Delete this course?")) return;
    await api.delete(`/courses/${id}`);
    toast.success("Course deleted");
    nav("/app/courses");
  };

  if (loading) return (
    <div>
      <div className="flex items-center justify-center h-64 text-[#B0AEBC]">Loading…</div>
    </div>
  );

  if (!course) return null;

  const avgMastery = course.studies?.length
    ? Math.round(course.studies.reduce((a, s) => a + (s.mastery_score || 0), 0) / course.studies.length)
    : 0;
  const totalDue = course.studies?.reduce((a, s) => a + (s.due_count || 0), 0) || 0;
  const totalTime = course.studies?.reduce((a, s) => a + (s.estimated_study_time_minutes || 0), 0) || 0;

  return (
    <div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back */}
        <Link to="/app/courses" className="flex items-center gap-1.5 text-[#B0AEBC] hover:text-white text-[13px] mb-6 w-fit">
          <ArrowLeft size={14} /> All Courses
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#B8A4FF]/20 to-[#4A90E2]/20 border border-[#B8A4FF]/20 flex items-center justify-center flex-shrink-0">
              <GraduationCap size={22} className="text-[#B8A4FF]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{course.title}</h1>
              {course.description && (
                <p className="text-[14px] text-[#B0AEBC] mt-1">{course.description}</p>
              )}
              <div className="flex items-center gap-4 mt-3 text-[12px] text-[#B0AEBC]">
                <span className="flex items-center gap-1"><BookOpen size={12} /> {course.study_ids?.length} studies</span>
                <span className="flex items-center gap-1"><Layers size={12} /> {course.total_flashcards} cards</span>
                <span className="flex items-center gap-1"><Clock size={12} /> ~{totalTime}m</span>
                <span className="flex items-center gap-1"><Star size={12} /> {avgMastery}% avg mastery</span>
              </div>
            </div>
          </div>
          <button onClick={deleteCourse}
            className="p-2 rounded-lg text-[#6B6B7B] hover:text-red-400 hover:bg-red-400/10 transition-colors">
            <Trash2 size={16} />
          </button>
        </div>

        {/* Stats summary */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#B8A4FF]">{avgMastery}%</p>
            <p className="text-[12px] text-[#B0AEBC] mt-1">Avg Mastery</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-orange-400">{totalDue}</p>
            <p className="text-[12px] text-[#B0AEBC] mt-1">Cards Due</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{course.total_flashcards}</p>
            <p className="text-[12px] text-[#B0AEBC] mt-1">Total Cards</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 mb-8">
          <Link
            to={`/app/courses/${id}/review`}
            className="btn btn-primary flex-1 py-3 flex items-center justify-center gap-2 text-[14px]"
            data-testid="course-review-btn"
          >
            <Layers size={16} /> Review All Cards
            {totalDue > 0 && (
              <span className="bg-white/20 text-white text-[11px] px-1.5 py-0.5 rounded-full">{totalDue} due</span>
            )}
          </Link>
          <button
            onClick={startExam}
            className="btn btn-outline flex-1 py-3 flex items-center justify-center gap-2 text-[14px]"
            data-testid="course-exam-btn"
          >
            <Trophy size={16} /> Combined Exam
          </button>
          <button
            onClick={() => setShowPublish(true)}
            className="btn btn-secondary px-4 py-3 flex items-center justify-center gap-2 text-[14px]"
            data-testid="course-publish-btn"
            title="Publish this course to the marketplace"
          >
            <Share2 size={16} /> Publish
          </button>
        </div>

        <AnimatePresence>
          {showPublish && (
            <CoursePublishDialog courseId={id} course={course} onClose={() => setShowPublish(false)} />
          )}
        </AnimatePresence>

        {/* Included Studies */}
        <div>
          <h2 className="text-[16px] font-semibold text-white mb-4">Included Studies</h2>
          <div className="space-y-2">
            {course.studies?.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="card p-4 flex items-center justify-between hover:border-[#3a3a4e] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#1a1a2e] flex items-center justify-center text-[13px] font-bold text-[#B8A4FF]">
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-white">{s.title}</p>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[#B0AEBC]">
                      <span>{s.flashcard_count} cards</span>
                      {s.due_count > 0 && <span className="text-orange-400">{s.due_count} due</span>}
                      <span className={`capitalize ${
                        s.status === "mastered" ? "text-green-400" :
                        s.status === "reviewed" ? "text-blue-400" :
                        "text-[#B0AEBC]"
                      }`}>{(s.status || "").replace(/_/g, " ")}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16">
                    <div className="flex justify-between mb-1">
                      <span className="text-[10px] text-[#B0AEBC]">Mastery</span>
                      <span className="text-[10px] text-white">{s.mastery_score}%</span>
                    </div>
                    <div className="h-1 bg-[#2a2a3e] rounded-full">
                      <div
                        className="h-1 bg-gradient-to-r from-[#B8A4FF] to-blue-400 rounded-full"
                        style={{ width: `${s.mastery_score || 0}%` }}
                      />
                    </div>
                  </div>
                  <Link to={`/app/studies/${s.id}`}
                    className="p-1.5 rounded text-[#B0AEBC] hover:text-white transition-colors">
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const COURSE_MAX_PRICE = 25;

function CoursePublishDialog({ courseId, course, onClose }) {
  const [price, setPrice] = useState(0);
  const [desc, setDesc] = useState(course?.description || "");
  const [publishing, setPublishing] = useState(false);
  const [done, setDone] = useState(null); // { url }

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/shared-packs/publish-course", {
        course_id: courseId,
        description: desc.trim() || null,
        price_gems: price,
      });
      const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const url = `${backend.replace(/\/+$/, "")}/api/p/${data.slug}`;
      setDone({ url, rarity: data.rarity });
      toast.success("Course published to the marketplace!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
      onClick={onClose} data-testid="course-publish-dialog"
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[460px] surface rounded-3xl p-7"
      >
        <button onClick={onClose} className="btn-icon absolute top-4 right-4" aria-label="Close"><X size={16} /></button>

        {!done ? (
          <>
            <div className="eyebrow mb-2">PUBLISH COURSE</div>
            <h3 className="text-[20px] font-semibold mb-1.5">Share “{course?.title}”</h3>
            <p className="text-[12.5px] text-[#8E8B99] leading-relaxed mb-6">
              Bundle all {course?.total_flashcards} cards from {course?.study_ids?.length} studies into one importable pack.
              Importers pay in gems — 100% goes to you.
            </p>

            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-[13px] text-[#B0AEBC]"><Gem size={13} className="text-[#B8A4FF]" /> Import price</div>
              <div className="text-[24px] font-bold tabular-nums text-[#B8A4FF]">{price === 0 ? "FREE" : `${price} gems`}</div>
            </div>
            <input type="range" min={0} max={COURSE_MAX_PRICE} value={price}
              onChange={(e) => setPrice(Number(e.target.value))} className="w-full accent-[#7C5CFF]" data-testid="course-price-slider" />
            <div className="flex justify-between text-[10.5px] text-[#8E8B99] mt-1 mb-5">
              <span>Free</span><span>Max {COURSE_MAX_PRICE}g for a course</span>
            </div>

            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} maxLength={500}
              placeholder="Describe the course — who's it for, what it covers."
              className="input resize-y mb-5" data-testid="course-publish-desc" />

            <button onClick={publish} disabled={publishing} className="btn btn-primary w-full py-3 text-[14px]" data-testid="course-publish-confirm">
              {publishing ? <><Loader2 size={14} className="animate-spin" /> Publishing…</> : <><Share2 size={14} /> Publish to Marketplace</>}
            </button>
          </>
        ) : (
          <div className="text-center py-2" data-testid="course-publish-success">
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ background: "linear-gradient(135deg,#7C5CFF,#5AC8FA)", boxShadow: "0 0 40px -8px rgba(124,92,255,0.7)" }}>
              <CheckCircle2 size={26} className="text-white" />
            </div>
            <h3 className="text-[20px] font-bold mb-1">Live on Explore!</h3>
            <p className="text-[13px] text-[#B0AEBC] mb-5">Share this link to grow your reach.</p>
            <div className="input flex items-center gap-2 mb-4">
              <code className="text-[11.5px] text-[#B8A4FF] truncate flex-1 text-left">{done.url}</code>
              <button onClick={() => { navigator.clipboard?.writeText(done.url); toast.success("Copied!"); }} className="btn-icon" aria-label="Copy"><Copy size={13} /></button>
            </div>
            <button onClick={onClose} className="btn btn-primary w-full py-2.5 text-[13px]">Done</button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
